import { Outlet } from 'react-router-dom';
import { useAuth } from '@wa/shared';
import { AvailabilityToggle } from '@/components/AvailabilityToggle';
import { IconRail } from '@/app/IconRail';
import { WalletChip } from '@/app/WalletChip';
import { RealtimeProvider } from '@/notifications/RealtimeProvider';
import { NotificationPrompt } from '@/notifications/NotificationPrompt';

/**
 * The frame every screen sits in.
 *
 * Rail on the left, header across the top of the content, screen below. The rail
 * is outside the header rather than under it so the header belongs to the
 * *screen* — which is what lets the inbox put a search box in it later without
 * the rail having to move.
 *
 * The rail is 56px and icon-only since M10. The balance chip stays in the header,
 * but no longer as Billing's only route: Billing has its own slot on the rail again,
 * and the chip is now what it always should have been — a number, with a shortcut.
 *
 * Below `lg` the rail becomes a bottom bar and the header keeps only what earns
 * its place: at 360px the availability switch and the balance both matter more
 * than the tenant's own name, which the agent knows.
 */
export function AppShell() {
  const { user, signOut } = useAuth();

  return (
    // The live connection wraps the whole shell, not the inbox.
    //
    // It used to live inside InboxPage, which meant it existed exactly while an
    // agent was looking at the chat screen — the one moment they do not need to be
    // told a message arrived. Here, one connection serves every screen, the tab
    // badge is correct on Reports, and a notification reaches somebody who is in
    // Settings. The inbox subscribes to it rather than owning it.
    <RealtimeProvider>
    <div className="flex h-full min-h-0 flex-col lg:flex-row">
      <IconRail />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex shrink-0 items-center gap-2 border-b border-(--color-line) bg-(--color-surface) px-3 py-2 sm:gap-3 sm:px-4">
          <Brand tenantName={user?.tenantName} />

          <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
            <WalletChip />

            {/* Below lg the rail is a bottom bar with no room for these, so the
                header keeps them; above it, they live at the foot of the rail. */}
            <div className="lg:hidden"><AvailabilityToggle /></div>

            <div className="hidden items-center gap-2 md:flex lg:hidden">
              <span className="max-w-32 truncate text-small text-(--color-muted)">
                {user?.displayName}
              </span>
              <button
                type="button"
                onClick={() => void signOut()}
                className="tap rounded-(--radius-field) border border-(--color-line) px-2.5 py-1 text-small text-(--color-muted) transition hover:bg-(--color-sunken) hover:text-(--color-ink)"
              >
                Sign out
              </button>
            </div>

            {/* Below md the name and the button do not fit beside the balance, so
                the initial is the whole control. */}
            <button
              type="button"
              onClick={() => void signOut()}
              aria-label={`Sign out of ${user?.displayName ?? 'your account'}`}
              className="tap grid size-8 shrink-0 place-items-center rounded-full bg-(--color-brand-wash) text-small font-semibold text-(--color-brand) md:hidden"
            >
              {(user?.displayName ?? '?').trim().charAt(0).toUpperCase()}
            </button>
          </div>
        </header>

        <NotificationPrompt />

        <main className="min-h-0 flex-1 overflow-hidden"><Outlet /></main>
      </div>
    </div>
    </RealtimeProvider>
  );
}

/**
 * A mark and a name.
 *
 * The mark is a speech bubble carrying a tick — the two things this product is —
 * drawn rather than lettered, because a wordmark set in the body font is exactly
 * what "unfinished" looks like. On the rail's own width at `lg` it aligns with the
 * navigation beneath it.
 */
function Brand({ tenantName }: { tenantName: string | null | undefined }) {
  return (
    // Hidden below sm. At 360px the balance, the availability switch and the
    // account button need every pixel of 336, and the three of them overflowed the
    // right-hand group *leftwards* — justify-end overflows towards the start — so
    // the wallet chip rendered on top of this mark. Nothing left the viewport, so
    // the overflow check passed and only a measurement of the boxes showed it.
    // The mark is decoration on a phone: the app is already open.
    <div className="hidden min-w-0 shrink items-center gap-2 sm:flex">
      <span
        aria-hidden="true"
        className="grid size-7 shrink-0 place-items-center rounded-(--radius-field) bg-(--color-brand) text-(--color-brand-ink)"
      >
        <svg viewBox="0 0 20 20" width="16" height="16">
          <path
            d="M10 3.2c3.8 0 6.9 2.5 6.9 5.7 0 3.1-3.1 5.6-6.9 5.6a8.6 8.6 0 0 1-2-.2l-3.1 1.5.7-2.6C4.1 12.2 3.1 10.7 3.1 8.9 3.1 5.7 6.2 3.2 10 3.2z"
            fill="currentColor" opacity="0.28"
          />
          <path
            d="M6.9 9.2 8.8 11l4.3-4.2"
            fill="none" stroke="currentColor" strokeWidth="1.9"
            strokeLinecap="round" strokeLinejoin="round"
          />
        </svg>
      </span>

      <span className="hidden min-w-0 truncate text-body font-semibold tracking-tight sm:block">
        {tenantName ?? 'PSM'}
      </span>
    </div>
  );
}
